import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet, Link } from "react-router-dom";
import {
  BarChart,
  Calendar,
  Settings,
  Bell as BellIcon,
  User,
  Users,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bot,
  Heart,
  Briefcase,
  Puzzle,
  Layers,
  TrendingUp,
  Sparkles,
  Settings2,
  Menu,
  Gauge,
  Newspaper,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Megaphone,
  X,
  Calculator,
  Shield,
  Database,
  Gift,
  GraduationCap,
  BookOpen,
  Scale,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { TrialBanner } from "../TrialBanner";
import { FeedbackModal } from "../FeedbackModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { Loader2 } from "lucide-react";
import LegalDisclaimer from "../LegalDisclaimer";
import Logo from "../Logo";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import RequestDemoModal from "../modals/RequestDemoModal";
import { ReferralModal } from "../modals/ReferralModal";
import { supabase } from "@/lib/supabase";
import { CreditBadge } from "../CreditBadge";
import { AgentProgressModal } from "./AgentProgressModal";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import { usePreventBack } from "@/hooks/usePreventBack";
import { NotificationBell } from "./NotificationBell";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";


const DashboardLayout = () => {
  usePresenceHeartbeat();
  // Prevent browser back button navigation while inside Dashboard
  usePreventBack({
    message: "La navegación hacia atrás está deshabilitada en el Dashboard.",
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const { toast } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth >= 768
  );
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const agentExec = useAgentExecution();
  const runs = agentExec?.runs || {};
  const getRun = agentExec?.getRun;
  const activeRunId = agentExec?.activeRunId;
  const trialStatus = useTrialStatus();
  const [storageUsage, setStorageUsage] = useState<any>(null);

  const loadStorageUsage = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL || "http://localhost:8001"}/api/v1/user/storage-usage`, {
        headers: {
          "Authorization": `Bearer ${token || ""}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setStorageUsage(data);
      }
    } catch (error) {
      console.error("Error loading storage usage:", error);
    }
  };

  const lastDlStorageRef = React.useRef<number>(0);
  const throttledLoadStorageUsage = React.useCallback(() => {
    const now = Date.now();
    if (now - lastDlStorageRef.current < 15000) return;
    lastDlStorageRef.current = now;
    loadStorageUsage();
  }, []);

  useEffect(() => {
    if (user?.id) {
      throttledLoadStorageUsage();
    }
  }, [user?.id]);

  useEffect(() => {
    window.addEventListener("storage-updated", throttledLoadStorageUsage);
    return () => {
      window.removeEventListener("storage-updated", throttledLoadStorageUsage);
    };
  }, []);

  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [dismissedCompletedRunIds, setDismissedCompletedRunIds] = useState<Record<string, boolean>>({});

  const getAgentLabel = (agentType: string | undefined) => {
    if (!agentType) return "Agente Legal";
    if (agentType.startsWith("specialized-")) return "Agente Especializado";
    switch (agentType) {
      case "campus": return "MIA Campus";
      case "consultorio": return "Consultorio Jurídico";
      case "campus-consultorio": return "Campus & Consultorio";
      case "legal-chatbot": return "SarAI (Chat)";
      case "document-drafting": return "Redacción Jurídica";
      case "contract-review": return "Revisión de Contratos";
      case "legal-research": return "Investigación Jurídica";
      case "case-prediction": return "Predicción de Casos";
      case "compliance-monitoring": return "Cumplimiento Normativo";
      case "patent-search": return "Búsqueda de Patentes";
      case "whistleblower": return "Canal de Denuncias";
      default: return "Agente Legal";
    }
  };

  const getAgentPath = (agentType: string | undefined) => {
    if (!agentType) return "/dashboard/agents";
    if (agentType.startsWith("specialized-")) {
      const agentId = agentType.replace("specialized-", "");
      return `/dashboard/agents/specialized/${agentId}`;
    }
    switch (agentType) {
      case "campus": return "/dashboard/campus";
      case "consultorio": return "/dashboard/consultorio";
      case "campus-consultorio": return "/dashboard/campus-consultorio";
      case "legal-chatbot": return "/dashboard/agents/legal-chatbot";
      case "document-drafting": return "/dashboard/agents/document-drafting-v2";
      case "contract-review": return "/dashboard/agents/contract-review-v2";
      case "legal-research": return "/dashboard/agents/legal-research-v2";
      case "case-prediction": return "/dashboard/agents/case-prediction-v2";
      case "compliance-monitoring": return "/dashboard/agents/compliance-monitoring-v2";
      case "patent-search": return "/dashboard/agents/patent-search-v2";
      case "whistleblower": return "/dashboard/agents/whistleblower-v2";
      default: return "/dashboard/agents";
    }
  };

  const allRunsArray = Object.values(runs);
  const activeRunsArray = allRunsArray.filter(r => r.status === "loading" || r.status === "streaming");
  const activeRun = (activeRunId && runs[activeRunId])
    ? runs[activeRunId]
    : activeRunsArray.length > 0
      ? activeRunsArray[activeRunsArray.length - 1]
      : undefined;
  const isAgentActive = Boolean(activeRun && (activeRun.status === "loading" || activeRun.status === "streaming"));

  const handleDismissCompletedRun = (runId?: string) => {
    setDismissedCompletedRunIds(prev => {
      const next = { ...prev };
      if (runId) {
        next[runId] = true;
      }
      allRunsArray.forEach(r => {
        if (r.status === "completed") {
          next[r.id] = true;
        }
      });
      return next;
    });
  };

  const completedRun = allRunsArray.slice().reverse().find(r => r.status === "completed" && !dismissedCompletedRunIds[r.id]);
  const isCompletedBannerVisible = Boolean(completedRun && !isAgentActive && location.pathname !== getAgentPath(completedRun.agentType));

  const activeRunSteps = activeRun?.traceSteps || [];
  const currentRunningStep = activeRunSteps.find(s => s.status === "running") || activeRunSteps[activeRunSteps.length - 1];
  const activeStepMessage = currentRunningStep
    ? `${currentRunningStep.emoji ? currentRunningStep.emoji + " " : ""}${typeof currentRunningStep.message === "object" ? JSON.stringify(currentRunningStep.message) : currentRunningStep.message}`
    : "Procesando...";

  const notificationsCount = 3;

  const customTitles: Record<string, string> = {
    "/dashboard": "Resumen",
    "/dashboard/agents": "Mis Agentes",
    "/dashboard/campus": "MIA Campus (Academia)",
    "/dashboard/consultorio": "Consultorio Jurídico (Ley 2113)",
    "/dashboard/campus-consultorio": "Campus & Consultorio Jurídico",
    // "/dashboard/agents/legal-chatbot-v2": "Chatbot RAG V2",
    "/dashboard/calendar": "Calendario",
    "/dashboard/notifications": "Notificaciones",
    "/dashboard/profile": "Configuración",
    "/dashboard/roi": "Panel de ROI",
    "/dashboard/admin/plans": "Gestión de Planes",
    "/dashboard/admin/ingest-registry": "Registro de Ingestas RAG",
    "/dashboard/subscription-usage": "Mi Suscripción",
    "/dashboard/boletines": "Boletines y Novedades",
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentHost = window.location.hostname;
    // Skip redirect in local development to avoid breaking the local dev environment
    const isLocalhost = currentHost === "localhost" || currentHost === "127.0.0.1" || currentHost.startsWith("192.168.");

    if (!isLocalhost && !currentHost.startsWith("app.")) {
      const protocol = window.location.protocol;
      const port = window.location.port ? `:${window.location.port}` : "";

      let newHost = currentHost;
      if (currentHost.startsWith("www.")) {
        newHost = currentHost.replace("www.", "app.");
      } else {
        newHost = `app.${currentHost}`;
      }

      // Redirect to the same path but on the 'app' subdomain
      window.location.href = `${protocol}//${newHost}${port}${location.pathname}${location.search}${location.hash}`;
    }
  }, [location.pathname, location.search, location.hash]);

  const handleLogout = async () => {
    try {
      await signOut();
      toast({
        title: "Sesión cerrada",
        description: "Has cerrado sesión exitosamente.",
      });
      const currentHost = window.location.hostname;
      const isLocalhost = currentHost === "localhost" || currentHost === "127.0.0.1" || currentHost.startsWith("192.168.");

      if (isLocalhost || !currentHost.startsWith("app.")) {
        navigate("/");
      } else {
        const protocol = window.location.protocol;
        const port = window.location.port ? `:${window.location.port}` : "";
        // Volver al dominio principal quitando el prefijo 'app.'
        const newHost = currentHost.replace(/^app\./, "");
        window.location.href = `${protocol}//${newHost}${port}/`;
      }
    } catch (error) {
      console.error("Error logging out:", error);
      toast({
        title: "Error",
        description: "No se pudo cerrar la sesión. Intente nuevamente.",
        variant: "destructive",
      });
    }
  };

  const getUserInitials = (name: string | null | undefined): string => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getTitleFromPath = (path: string): string => {
    if (customTitles[path]) return customTitles[path];
    const pathSegments = path.split("/").filter(Boolean);
    if (pathSegments.length < 2) return "Resumen";
    const lastSegment = pathSegments[pathSegments.length - 1];

    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastSegment);
    const isRadicado = /^\d{23}$/.test(lastSegment.replace(/\s/g, ""));
    if (isUuid || isRadicado) {
      return "Expediente";
    }

    return lastSegment
      .replace(/-/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const buildBreadcrumbs = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    let path = "";
    return pathSegments.map((segment, idx) => {
      path += `/${segment}`;

      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
      const isRadicado = /^\d{23}$/.test(segment.replace(/\s/g, ""));
      const isDynamicId = isUuid || isRadicado;

      const display =
        customTitles[path] ||
        (isDynamicId ? "Expediente" : segment.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()));
      return {
        name: display,
        path,
        isLast: idx === pathSegments.length - 1,
      };
    });
  };

  // ── Verificación de Rol Admin ──────────────────────────────
  const isAdmin = profile?.role === "admin" || (user as any)?.user_metadata?.role === "admin";

  const mainNavItems = [
    {
      label: "Resumen",
      icon: BarChart,
      path: "/dashboard",
      isActive: location.pathname === "/dashboard",
      gradient: "from-blue-500 to-indigo-600",
    },
    {
      label: "Mis Agentes",
      icon: Bot,
      path: "/dashboard/agents",
      isActive: location.pathname === "/dashboard/agents" || location.pathname.startsWith("/dashboard/agents/"),
      gradient: "from-violet-500 to-purple-600",
    },
    {
      label: "MIA Campus",
      icon: BookOpen,
      path: "/dashboard/campus",
      isActive: location.pathname === "/dashboard/campus" || location.pathname.startsWith("/dashboard/agents/campus"),
      gradient: "from-indigo-600 to-violet-600",
      hide: !isAdmin,
    },
    {
      label: "Consultorio Jurídico",
      icon: Scale,
      path: "/dashboard/consultorio",
      isActive: location.pathname === "/dashboard/consultorio" || location.pathname.startsWith("/dashboard/agents/consultorio"),
      gradient: "from-emerald-600 to-teal-700",
      hide: !isAdmin,
    },
    {
      label: "Despachos",
      icon: Briefcase,
      path: "/dashboard/despachos",
      isActive: location.pathname.startsWith("/dashboard/despachos"),
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      label: "Calculadoras",
      icon: Calculator,
      path: "/dashboard/calculadoras",
      isActive: location.pathname.startsWith("/dashboard/calculadoras"),
      gradient: "from-amber-500 to-amber-600",
    },
    {
      label: "Calendario",
      icon: Calendar,
      path: "/dashboard/calendar",
      isActive: location.pathname === "/dashboard/calendar",
      gradient: "from-amber-500 to-orange-600",
    },
    {
      label: "Mi Suscripción",
      icon: Gauge,
      path: "/dashboard/subscription-usage",
      isActive: location.pathname === "/dashboard/subscription-usage",
      gradient: "from-cyan-500 to-blue-600",
    },
    {
      label: "Boletines",
      icon: Newspaper,
      path: "/dashboard/boletines",
      isActive: location.pathname.startsWith("/dashboard/boletines"),
      gradient: "from-pink-500 to-rose-600",
      hide: !(isAdmin || profile?.role === "contributor"),
    },
    {
      label: "Configuración",
      icon: Settings,
      path: "/dashboard/settings",
      isActive: location.pathname === "/dashboard/settings",
      gradient: "from-slate-500 to-gray-600",
    },
  ].filter((item) => !item.hide);

  const adminNavItems = [
    {
      label: "Registro de Ingestas (RAG)",
      icon: Database,
      path: "/dashboard/admin/ingest-registry",
      isActive: location.pathname === "/dashboard/admin/ingest-registry",
      gradient: "from-indigo-600 to-blue-700",
    },
    {
      label: "Gestión de Planes",
      icon: Settings2,
      path: "/dashboard/admin/plans",
      isActive: location.pathname === "/dashboard/admin/plans",
      gradient: "from-indigo-600 to-blue-700",
    },
    {
      label: "Usuarios Conectados",
      icon: Users,
      path: "/dashboard/admin/online-users",
      isActive: location.pathname === "/dashboard/admin/online-users",
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      label: "Banners Admin",
      icon: Megaphone,
      path: "/dashboard/admin/banners",
      isActive: location.pathname === "/dashboard/admin/banners",
      gradient: "from-amber-500 to-orange-600",
    },
  ];

  const disabledItems = [
    // { label: "Notificaciones", icon: BellIcon, badge: notificationsCount },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <div
        className={cn(
          "fixed md:relative z-30 h-screen transition-all duration-300 ease-in-out",
          isSidebarOpen
            ? "w-72 max-w-[85vw] md:w-64"
            : "w-0 md:w-20 overflow-hidden"
        )}>
        <div className="flex h-full flex-col bg-white border-r border-gray-100">
          {/* Header */}
          <div
            className={cn(
              "py-3 px-4 border-b border-gray-100",
              !isSidebarOpen && "md:p-3"
            )}>
            <div className="flex items-center justify-between">
              <div
                className={cn(
                  "transition-all duration-300",
                  !isSidebarOpen && "md:flex md:justify-center w-full"
                )}>
                <Link to="/dashboard">
                  <Logo
                    iconOnly={!isSidebarOpen}
                    className={cn(
                      "transition-all duration-300",
                      isSidebarOpen ? "text-base" : "text-sm"
                    )}
                  />
                </Link>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="ml-2 hover:bg-gray-100 rounded-xl text-slate-500 hover:text-blue-600 transition-colors"
                onClick={toggleSidebar}
                title={isSidebarOpen ? "Colapsar panel lateral" : "Expandir panel lateral"}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /></svg>
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 px-3 py-2.5 overflow-y-auto custom-scrollbar flex flex-col gap-2.5 min-h-0">
            <nav className="space-y-1">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const isRestrictedByTrial =
                  trialStatus.isExpired &&
                  ["Mis Agentes", "Despachos", "Calendario"].includes(item.label);

                return (
                  <Button
                    key={item.label}
                    variant="ghost"
                    disabled={isRestrictedByTrial}
                    className={cn(
                      "w-full justify-start rounded-xl transition-all duration-200 h-9 group relative",
                      item.isActive
                        ? "bg-legal-blue text-white hover:bg-legal-blue/90 shadow-md shadow-legal-blue/20"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
                      isRestrictedByTrial && "opacity-50 grayscale cursor-not-allowed",
                      !isSidebarOpen && "md:justify-center md:px-2"
                    )}
                    onClick={() => !isRestrictedByTrial && navigate(item.path)}>
                    <div className={cn(
                      "flex items-center justify-center rounded-lg transition-all duration-200",
                      isSidebarOpen ? "mr-2.5" : "mr-0",
                    )}>
                      <Icon className="h-[18px] w-[18px]" />
                    </div>
                    {isSidebarOpen && (
                      <span className="text-sm font-medium">{item.label}</span>
                    )}
                  </Button>
                );
              })}
            </nav>

            {/* Sección Exclusiva para ROLE: 'admin' */}
            {isAdmin && (
              <div className="pt-3 pb-1 border-t border-gray-100">
                <div className="flex items-center gap-2 px-3 py-1.5 mb-1">
                  <Shield className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                  {isSidebarOpen && (
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Administración
                    </span>
                  )}
                </div>
                <nav className="space-y-1">
                  {adminNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Button
                        key={item.label}
                        variant="ghost"
                        className={cn(
                          "w-full justify-start rounded-xl transition-all duration-200 h-9 group relative",
                          item.isActive
                            ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-600/20"
                            : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-900",
                          !isSidebarOpen && "md:justify-center md:px-2"
                        )}
                        onClick={() => navigate(item.path)}>
                        <div className={cn(
                          "flex items-center justify-center rounded-lg transition-all duration-200",
                          isSidebarOpen ? "mr-2.5" : "mr-0",
                        )}>
                          <Icon className="h-[18px] w-[18px]" />
                        </div>
                        {isSidebarOpen && (
                          <span className="text-sm font-medium">{item.label}</span>
                        )}
                      </Button>
                    );
                  })}
                </nav>
              </div>
            )}

            {/* Referral Banner / Button */}
            {isSidebarOpen ? (
              <div className="p-3 bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-100 rounded-xl mt-1.5 animate-fade-in">
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="h-3.5 w-3.5 text-violet-600 animate-pulse" />
                  <span className="text-[11px] font-bold text-violet-900">Refiere y Gana</span>
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[8px] font-black px-1.5 py-0 h-3.5 ml-auto border-none">
                    $10.000 COP
                  </Badge>
                </div>
                <p className="text-[10px] text-gray-500 mb-2 leading-relaxed">
                  Gana $10.000 de descuento en tu renovación por cada amigo que se suscriba.
                </p>
                <Button
                  size="sm"
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold text-[10px] h-7 rounded-lg transition-all shadow-sm"
                  onClick={() => setShowReferralModal(true)}
                >
                  Invitar Amigos
                </Button>
              </div>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-full h-9 rounded-xl text-gray-600 hover:bg-violet-50 hover:text-violet-700 flex justify-center mt-1"
                      onClick={() => setShowReferralModal(true)}
                    >
                      <Gift className="h-[18px] w-[18px] text-violet-600 animate-pulse" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Refiere y Gana $10.000</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Demo Request Sidebar Card */}
            {isSidebarOpen ? (
              <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl mt-1.5 animate-fade-in">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-3.5 w-3.5 text-blue-600 animate-pulse" />
                  <span className="text-[11px] font-bold text-blue-900">Demo Personalizada</span>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-[8px] px-1.5 py-0 h-3.5 ml-auto border-none">
                    Gratis
                  </Badge>
                </div>
                <p className="text-[10px] text-gray-500 mb-2 leading-relaxed">
                  Descubre cómo nuestra IA legal puede transformar tu práctica en 30 minutos.
                </p>
                <Button
                  size="sm"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-[10px] h-7 rounded-lg transition-all shadow-sm"
                  onClick={() => setShowDemoModal(true)}
                >
                  Solicitar Demo
                </Button>
              </div>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-full h-9 rounded-xl text-gray-600 hover:bg-gray-100 hover:text-gray-900 flex justify-center mt-1"
                      onClick={() => setShowDemoModal(true)}
                    >
                      <Calendar className="h-[18px] w-[18px] text-blue-600 animate-pulse" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Solicitar Demo Personalizada</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Background Completed Agent Indicator */}
            {completedRun && !isAgentActive && (
              <div
                className={cn(
                  "mt-3 p-3 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 dark:from-slate-800 dark:to-emerald-950/40 dark:border-emerald-900/60 transition-all hover:shadow-md cursor-pointer animate-fade-in",
                  !isSidebarOpen && "md:px-2 md:py-2.5 flex justify-center"
                )}
                onClick={() => {
                  handleDismissCompletedRun(completedRun.id);
                  const path = getAgentPath(completedRun.agentType);
                  const query = completedRun.agentType === 'legal-chatbot' ? `?chat_id=${completedRun.id}` : '';
                  navigate(path + query);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  </div>
                  {isSidebarOpen && (
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest truncate">
                        {getAgentLabel(completedRun.agentType)} Listo
                      </p>
                      <p className="text-xs text-gray-700 dark:text-gray-300 font-semibold truncate flex items-center gap-1">
                        Ver Resultado <ChevronRight className="h-3 w-3 text-emerald-600" />
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {isAgentActive && (
              <div
                className={cn(
                  "mt-3 p-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 dark:from-slate-800 dark:to-blue-950/40 dark:border-blue-900/60 transition-all hover:shadow-md cursor-pointer",
                  !isSidebarOpen && "md:px-2 md:py-2.5 flex justify-center"
                )}
                onClick={() => setIsProgressModalOpen(true)}
              >
                <div className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500"></span>
                    </span>
                  </div>
                  {isSidebarOpen && (
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest truncate">
                        {getAgentLabel(activeRun?.agentType)} Activo
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-300 font-medium truncate">
                        {activeStepMessage}
                      </p>
                    </div>
                  )}
                </div>
                {isSidebarOpen && (
                  <Button
                    variant="link"
                    className="h-auto p-0 mt-2 text-[11px] text-blue-600 font-bold hover:text-blue-800 flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsProgressModalOpen(true);
                    }}
                  >
                    Ver progreso <ChevronRight className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}

            {/* Trial Banner inside scroll container */}
            {isSidebarOpen && (
              <div className="mt-2 animate-fade-in">
                <TrialBanner />
              </div>
            )}

            {/* Divider */}
            <hr className="border-gray-100 my-2" />

            {/* User section inside scroll container */}
            <div className={cn("space-y-2 mt-1.5 pb-2", !isSidebarOpen && "flex flex-col items-center")}>
              <div className="flex items-center gap-2">
                <Avatar className={cn(
                  "ring-2 ring-gray-100",
                  !isSidebarOpen && "md:w-8 md:h-8"
                )}>
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-legal-blue to-legal-navy text-white text-sm font-bold">
                    {getUserInitials(profile?.full_name)}
                  </AvatarFallback>
                </Avatar>
                {isSidebarOpen && (
                  <div className="truncate">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {profile?.full_name || user?.email}
                    </p>
                    <p className="text-xs text-gray-400 capitalize">
                      {profile?.role || "Usuario"}
                    </p>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start rounded-xl hover:bg-red-50 hover:text-red-600 transition-colors h-9 text-gray-500",
                  !isSidebarOpen && "md:justify-center md:px-2"
                )}
                onClick={handleLogout}>
                <LogOut className={cn("h-4 w-4", isSidebarOpen ? "mr-2" : "mr-0")} />
                {isSidebarOpen && <span className="text-sm">Cerrar Sesión</span>}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 min-w-0 overflow-hidden bg-gray-50">
        <MaintenanceBanner />
        {/* Global Storage Quota Alert Banner */}
        {storageUsage && storageUsage.percentage >= 80 && (
          <div className={`shrink-0 px-4 sm:px-6 py-2.5 flex items-center justify-between text-xs select-none border-b font-medium animate-in slide-in-from-top duration-300 z-30 ${storageUsage.percentage >= 100
            ? "bg-red-50 text-red-800 border-red-100"
            : "bg-amber-50 text-amber-800 border-amber-100"
            }`}>
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-4.5 w-4.5 shrink-0 ${storageUsage.percentage >= 100 ? "text-red-600 animate-pulse" : "text-amber-500"}`} />
              <span>
                {storageUsage.percentage >= 100 ? (
                  <><strong>🚨 Límite de Almacenamiento Superado (5 GB):</strong> Has alcanzado el <strong>{storageUsage.percentage}%</strong> ({storageUsage.total_mb} MB) de tu cuota permitida. La subida de nuevos archivos está bloqueada.</>
                ) : (
                  <><strong>⚠️ Alerta de Almacenamiento (Cuota de 5 GB):</strong> Has utilizado el <strong>{storageUsage.percentage}%</strong> ({storageUsage.total_mb} MB) de tu espacio total.</>
                )}
              </span>
            </div>
          </div>
        )}
        {/* Mobile Header */}
        <div className="flex md:hidden items-center justify-between px-4 py-2 bg-white border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-slate-500 hover:bg-gray-100 rounded-xl h-8 w-8"
              onClick={toggleSidebar}
              aria-label="Abrir panel lateral"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Logo iconOnly className="text-sm" />
            <span className="font-bold text-sm text-slate-800">
              {getTitleFromPath(location.pathname)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CreditBadge />
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="h-8 w-8 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-xl"
              title="Cerrar sesión"
            >
              <LogOut className="h-4 w-4" />
            </Button>
            <Avatar className="w-8 h-8 ring-2 ring-gray-100">
              <AvatarImage src={user?.user_metadata?.avatar_url} />
              <AvatarFallback className="bg-gradient-to-br from-legal-blue to-legal-navy text-white text-[10px] font-bold">
                {getUserInitials(profile?.full_name)}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>

        {/* Top Header Bar / Breadcrumb */}
        <div className="hidden md:flex items-center justify-between py-1.5 px-6 border-b border-gray-100 bg-white/60 backdrop-blur-sm sticky top-0 z-10">
          <nav
            className="flex items-center text-xs text-gray-400"
            aria-label="Breadcrumb">
            {buildBreadcrumbs().map((crumb, idx, arr) => (
              <span key={crumb.path} className="flex items-center">
                {crumb.isLast ? (
                  <span className="font-semibold text-gray-900">
                    {crumb.name}
                  </span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="hover:text-legal-blue text-gray-400 transition-colors">
                    {crumb.name}
                  </Link>
                )}
                {idx < arr.length - 1 && (
                  <ChevronRight className="mx-2 h-3.5 w-3.5 text-gray-300" />
                )}
              </span>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <CreditBadge />
            <div className="h-4 w-px bg-gray-200 mx-0.5" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="h-8 px-2.5 text-xs text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors font-medium flex items-center gap-1.5 cursor-pointer"
              title="Cerrar sesión"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Cerrar Sesión</span>
            </Button>
          </div>
        </div>

        {/* Completed Agent Banner */}
        {isCompletedBannerVisible && completedRun && (
          <div className="mx-3 sm:mx-6 mt-4 p-3.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 rounded-xl shadow-lg shadow-emerald-600/20 text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between animate-fade-in border border-emerald-400/30">
            <div
              className="flex min-w-0 items-center gap-3 cursor-pointer"
              onClick={() => {
                setIsProgressModalOpen(true);
              }}
            >
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm shrink-0">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold truncate">
                    ¡{getAgentLabel(completedRun.agentType)} ha finalizado con éxito!
                  </p>
                  <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white">
                    Completado
                  </span>
                </div>
                <p className="text-xs text-emerald-100 truncate font-medium mt-0.5">
                  El análisis se encuentra listo. Haz clic para ir al resultado.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="w-full bg-white text-emerald-800 hover:bg-emerald-50 font-bold px-4 rounded-lg sm:w-auto shadow-sm flex items-center gap-1.5"
                onClick={() => {
                  handleDismissCompletedRun(completedRun.id);
                  const path = getAgentPath(completedRun.agentType);
                  const query = completedRun.agentType === 'legal-chatbot' ? `?chat_id=${completedRun.id}` : '';
                  navigate(path + query);
                }}
              >
                Ver Resultado <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 h-8 w-8 rounded-lg shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismissCompletedRun(completedRun.id);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Background Agent Banner */}
        {isAgentActive && location.pathname !== getAgentPath(activeRun?.agentType) && (
          <div className="mx-3 sm:mx-6 mt-4 p-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 rounded-xl shadow-lg shadow-blue-600/20 text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between animate-fade-in border border-blue-400/30">
            <div
              className="flex min-w-0 items-center gap-3 cursor-pointer"
              onClick={() => setIsProgressModalOpen(true)}
            >
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm shrink-0">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold truncate">
                    {getAgentLabel(activeRun?.agentType)} en segundo plano
                  </p>
                  <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white">
                    Activo
                  </span>
                </div>
                <p className="text-xs text-blue-100 truncate font-medium mt-0.5">
                  {activeStepMessage}
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold px-4 rounded-lg sm:w-auto shadow-sm"
              onClick={() => setIsProgressModalOpen(true)}
            >
              Ver progreso
            </Button>
          </div>
        )}

        <main className="flex-1 flex flex-col h-full min-h-0 relative">
          <div className={cn(
            "flex-1 min-h-0 custom-scrollbar",
            (location.pathname.includes("legal-chatbot") || location.pathname.includes("campus") || location.pathname.includes("consultorio") || location.pathname.includes("collab") || location.pathname.includes("editor") || location.pathname.includes("/run"))
              ? "overflow-hidden h-full flex flex-col pb-0 md:pb-0"
              : "overflow-y-auto pb-28 md:pb-0"
          )}>
            <Outlet />
          </div>
          {!(location.pathname.includes("legal-chatbot") || location.pathname.includes("campus") || location.pathname.includes("consultorio") || location.pathname.includes("collab") || location.pathname.includes("editor") || location.pathname.includes("/run")) && (
            <div className="px-3 py-2 sm:px-6 mt-auto hidden md:block">
              <LegalDisclaimer variant="subtle" className="opacity-80 hover:opacity-100 transition-opacity !max-w-full" />
            </div>
          )}

          {/* Bottom Mobile Navigation Bar */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-gray-200/80 dark:border-slate-800 px-2 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] flex items-center justify-around shadow-lg">
            {[
              { label: "Resumen", icon: BarChart, path: "/dashboard" },
              { label: "Agentes", icon: Bot, path: "/dashboard/agents" },
              { label: "Procesos", icon: Briefcase, path: "/dashboard/despachos/procesos" },
              { label: "Calendario", icon: Calendar, path: "/dashboard/calendar" },
              { label: "Suscripción", icon: Gauge, path: "/dashboard/subscription-usage" },
            ].map((item) => {
              const Icon = item.icon;
              const isActive =
                item.path === "/dashboard"
                  ? location.pathname === "/dashboard"
                  : location.pathname.startsWith(item.path);

              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "flex flex-col items-center justify-center px-2 py-1 rounded-xl transition-all duration-200 min-w-[56px]",
                    isActive
                      ? "text-blue-600 font-bold bg-blue-50/80 dark:bg-blue-950/50 scale-105"
                      : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white font-medium"
                  )}
                >
                  <Icon className={cn("h-5 w-5 mb-0.5", isActive ? "text-blue-600" : "text-slate-400")} />
                  <span className="text-[10px] leading-none tracking-tight">{item.label}</span>
                </button>
              );
            })}
          </div>
        </main>


      </div>

      {/* Floating Feedback Button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="hidden md:flex fixed bottom-4 right-4 rounded-full h-9 w-9 shadow-lg border border-slate-200/80 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-all duration-300 hover:scale-110 z-30 items-center justify-center"
              size="icon"
              onClick={() => setShowFeedbackModal(true)}>
              <Heart className="h-4.5 w-4.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-[10px] font-bold">Comparte tu experiencia</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-20 md:hidden transition-opacity duration-300"
          onClick={toggleSidebar}
        />
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />

      {/* Request Demo Modal */}
      <RequestDemoModal
        isOpen={showDemoModal}
        onClose={() => setShowDemoModal(false)}
      />

      {/* Referral Modal */}
      <ReferralModal
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
      />

      {/* Agent Progress Modal */}
      <AgentProgressModal
        isOpen={isProgressModalOpen}
        onClose={() => setIsProgressModalOpen(false)}
        runId={activeRun?.id}
        getAgentLabel={getAgentLabel}
        getAgentPath={getAgentPath}
      />
    </div>
  );
};

export default DashboardLayout;
